import { Member, Contribution, Event, Expense, Town } from '../types';
import { sanitizeInput } from '../utils/sanitize';

// Professional template configuration
export interface PDFReportTemplate {
  header: {
    logo?: string;
    title: string;
    dateRange: string;
    townName: string;
    generatedDate: string;
  };
  styles: {
    colors: {
      primary: string;
      secondary: string;
      text: string;
      background: string;
      accent: string;
    };
    fonts: {
      header: string;
      body: string;
      monospace: string;
    };
  };
  layout: {
    margins: { top: number; right: number; bottom: number; left: number };
    columns: number;
    pageOrientation: 'portrait' | 'landscape';
  };
}

// Chart data interface
export interface ChartDataPoint {
  time: string;
  revenue: number;
  members: number;
  activities: number;
  contributions: number;
  benefits: number;
  events: number;
  expenses: number;
}

// PDF generation service (Backend version without React components)
export class PDFExportService {
  private defaultTemplate: PDFReportTemplate = {
    header: {
      title: 'Ghana Town Membership System Report',
      dateRange: '',
      townName: 'All Towns',
      generatedDate: new Date().toLocaleDateString()
    },
    styles: {
      colors: {
        primary: '#2563eb',    // Blue
        secondary: '#1e40af',  // Dark Blue
        text: '#1f2937',       // Gray 800
        background: '#ffffff',
        accent: '#f3f4f6'      // Gray 100
      },
      fonts: {
        header: 'Roboto',
        body: 'Roboto',
        monospace: 'Courier'
      }
    },
    layout: {
      margins: { top: 40, right: 40, bottom: 40, left: 40 },
      columns: 12,
      pageOrientation: 'portrait'
    }
  };

  // Generate Members Report PDF data structure
  generateMembersReportData(
    members: Member[],
    towns: Town[],
    selectedTownId?: string,
    template?: PDFReportTemplate
  ): any {
    const currentTemplate = { ...this.defaultTemplate, ...template };
    const filteredMembers = selectedTownId && selectedTownId !== 'all' 
      ? members.filter(m => m.townId === selectedTownId)
      : members;
    
    const townName = selectedTownId === 'all' 
      ? 'All Towns' 
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';

    currentTemplate.header.title = 'Members Report';
    currentTemplate.header.townName = townName;
    currentTemplate.header.dateRange = `Generated on ${new Date().toLocaleDateString()}`;

    // Return data structure for PDF generation
    return {
      type: 'members',
      template: currentTemplate,
      data: {
        members: filteredMembers.map(member => {
          const town = towns.find(t => t.id === member.townId);
          return {
            username: sanitizeInput(member.username),
            name: sanitizeInput(member.name),
            phone: sanitizeInput(member.phone),
            gender: sanitizeInput(member.gender),
            ageGroup: sanitizeInput(member.ageGroup),
            town: sanitizeInput(town?.name || 'Unknown'),
            status: sanitizeInput(member.status)
          };
        }),
        summary: {
          totalMembers: filteredMembers.length,
          activeMembers: filteredMembers.filter(m => m.status === 'active').length,
          maleMembers: filteredMembers.filter(m => m.gender === 'male').length,
          femaleMembers: filteredMembers.filter(m => m.gender === 'female').length
        }
      }
    };
  }

  // Generate Financial Report PDF data structure
  generateFinancialReportData(
    contributions: Contribution[],
    expenses: Expense[],
    towns: Town[],
    members: Member[],
    selectedTownId?: string,
    template?: PDFReportTemplate
  ): any {
    const currentTemplate = { ...this.defaultTemplate, ...template };
    const filteredContributions = selectedTownId && selectedTownId !== 'all'
      ? contributions.filter(c => c.townId === selectedTownId)
      : contributions;
    
    const filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    const townName = selectedTownId === 'all'
      ? 'All Towns'
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';

    currentTemplate.header.title = 'Financial Report';
    currentTemplate.header.townName = townName;
    currentTemplate.header.dateRange = `Generated on ${new Date().toLocaleDateString()}`;

    // Calculate financial summary
    const totalRevenue = filteredContributions.reduce((sum, c) => sum + c.amount, 0);
    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
    const balance = totalRevenue - totalExpenses;

    // Track missing member data for logging
    let missingMemberCount = 0;
    const missingMemberIds = new Set<string>();

    return {
      type: 'financial',
      template: currentTemplate,
      data: {
        contributions: filteredContributions.map(contribution => {
          const member = members.find(m => m.id === contribution.memberId);
          
          // Log missing member data
          if (!member) {
            missingMemberCount++;
            missingMemberIds.add(contribution.memberId);
            console.warn(`PDF Export: Missing member data for memberId: ${contribution.memberId}`);
          }
          
          return {
            date: new Date(contribution.date).toLocaleDateString(),
            memberName: sanitizeInput(member?.name || 'Unknown Member'),
            type: sanitizeInput(contribution.type),
            amount: contribution.amount,
            status: sanitizeInput(contribution.status)
          };
        }),
        expenses: filteredExpenses.map(expense => ({
          date: new Date(expense.date).toLocaleDateString(),
          category: sanitizeInput(expense.category),
          description: sanitizeInput(expense.description),
          amount: expense.amount,
          beneficiary: sanitizeInput(expense.beneficiary || 'N/A')
        })),
        summary: {
          totalRevenue,
          totalExpenses,
          balance
        }
      }
    };
  }

  // Generate PDF with charts data structure
  generateReportWithChartsData(
    data: any[],
    chartData: ChartDataPoint[],
    reportType: 'members' | 'financial' | 'expenses' | 'events',
    template?: PDFReportTemplate
  ): any {
    const currentTemplate = { ...this.defaultTemplate, ...template };

    return {
      type: 'charts',
      template: currentTemplate,
      data: {
        reportType,
        chartData,
        tableData: data,
        generatedDate: new Date().toLocaleDateString()
      }
    };
  }

  // Get default template
  getDefaultTemplate(): PDFReportTemplate {
    return { ...this.defaultTemplate };
  }

  // Update default template
  updateDefaultTemplate(template: Partial<PDFReportTemplate>): void {
    this.defaultTemplate = { ...this.defaultTemplate, ...template };
  }

  // Generate Members Report PDF (NEW METHOD)
  async generateMembersReport(
    members: Member[],
    towns: Town[],
    selectedTownId?: string,
    template?: PDFReportTemplate
  ): Promise<Blob> {
    try {
      const reportData = this.generateMembersReportData(members, towns, selectedTownId, template);
      
      // Create PDF using jsPDF
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({
        orientation: reportData.template.layout.pageOrientation,
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = 40;

      // Add header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(37, 99, 235); // Primary blue
      doc.text(reportData.template.header.title, margin, yPosition);
      
      yPosition += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55); // Gray 800
      doc.text(`Town: ${reportData.template.header.townName}`, margin, yPosition);
      doc.text(`Generated: ${reportData.template.header.generatedDate}`, margin + 100, yPosition);
      yPosition += 15;

      // Add summary
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Summary', margin, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Total Members: ${reportData.data.summary.totalMembers}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Active Members: ${reportData.data.summary.activeMembers}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Male Members: ${reportData.data.summary.maleMembers}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Female Members: ${reportData.data.summary.femaleMembers}`, margin, yPosition);
      yPosition += 15;

      // Add table headers
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const headers = ['Username', 'Name', 'Phone', 'Gender', 'Age Group', 'Town', 'Status'];
      const colWidths = [25, 35, 30, 20, 25, 30, 25];
      let xPosition = margin;

      headers.forEach((header, index) => {
        doc.text(header, xPosition, yPosition);
        xPosition += colWidths[index];
      });
      yPosition += 8;

      // Add data rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      
      reportData.data.members.forEach(member => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }

        xPosition = margin;
        const values = [
          member.username,
          member.name,
          member.phone,
          member.gender,
          member.ageGroup,
          member.town,
          member.status
        ];

        values.forEach((value, index) => {
          const cellText = String(value || '');
          const lines = doc.splitTextToSize(cellText, colWidths[index] - 2);
          doc.text(lines, xPosition + 1, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 8;
      });

      // Add footer
      yPosition += 10;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175); // Gray 500
      doc.text(`Generated by Ghana Town Membership System • ${new Date().toLocaleDateString()}`, margin, pageHeight - 10);

      // Return as Blob
      const pdfBlob = new Blob([doc.output('blob')], { type: 'application/pdf' });
      return pdfBlob;
    } catch (error) {
      console.error('PDF generation error:', error);
      throw new Error('Failed to generate PDF report');
    }
  }

  // Generate Single Member Financial Report PDF (NEW METHOD)
  async generateSingleMemberFinancialReport(
    reportData: {
      member: Member,
      contributions: Array<{
        date: string,
        type: string,
        amount: number,
        paymentMethod: string,
        status: string
      }>,
      benefits: Array<{
        requestDate: string,
        type: string,
        amount: number,
        status: string
      }>,
      contributionSummary: {
        totalContributions: number,
        approvedContributions: number,
        pendingContributions: number,
        rejectedContributions: number,
        contributionCount: number
      },
      benefitSummary: {
        totalBenefitsRequested: number,
        totalBenefitsReceived: number,
        pendingBenefits: number,
        approvedBenefits: number,
        rejectedBenefits: number,
        benefitCount: number
      },
      financialSummary: {
        netContributions: number,
        overallBalance: number
      },
      generatedDate: string
    },
    template?: PDFReportTemplate
  ): Promise<Blob> {
    try {
      const currentTemplate = { ...this.defaultTemplate, ...template };
      
      // Update template for single member report
      currentTemplate.header.title = `Financial Report - ${reportData.member.name}`;
      currentTemplate.header.townName = 'Individual Member Report';
      currentTemplate.header.dateRange = `Generated on ${reportData.generatedDate}`;

      // Create PDF using jsPDF
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({
        orientation: currentTemplate.layout.pageOrientation,
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = 40;

      // Add header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(37, 99, 235); // Primary blue
      doc.text(currentTemplate.header.title, margin, yPosition);
      
      yPosition += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55); // Gray 800
      doc.text(`Member ID: ${reportData.member.username}`, margin, yPosition);
      doc.text(`Phone: ${reportData.member.phone || 'N/A'}`, margin + 100, yPosition);
      yPosition += 8;
      doc.text(`Generated: ${reportData.generatedDate}`, margin, yPosition);
      yPosition += 15;

      // Add member info section
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Member Information', margin, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Name: ${reportData.member.name}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Username: ${reportData.member.username}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Phone: ${reportData.member.phone || 'N/A'}`, margin, yPosition);
      yPosition += 15;

      // Add financial summary
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Financial Summary', margin, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Total Contributions: GH₵${reportData.contributionSummary.totalContributions.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Approved Contributions: GH₵${reportData.contributionSummary.approvedContributions.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Pending Contributions: GH₵${reportData.contributionSummary.pendingContributions.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Rejected Contributions: GH₵${reportData.contributionSummary.rejectedContributions.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Total Benefits Requested: GH₵${reportData.benefitSummary.totalBenefitsRequested.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Total Benefits Received: GH₵${reportData.benefitSummary.totalBenefitsReceived.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      
      // Add summary totals with highlighting
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(26, 102, 51); // Green for positive values
      doc.text(`Net Contributions: GH₵${reportData.financialSummary.netContributions.toFixed(2)}`, margin, yPosition);
      yPosition += 8;
      
      const balanceColor = reportData.financialSummary.overallBalance >= 0 ? [26, 102, 51] : [220, 38, 38];
      doc.setTextColor(...balanceColor);
      doc.text(`Overall Balance: GH₵${reportData.financialSummary.overallBalance.toFixed(2)}`, margin, yPosition);
      yPosition += 15;
      
      // Reset text color
      doc.setTextColor(31, 41, 55);

      // Add contributions section
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Contributions', margin, yPosition);
      yPosition += 10;

      if (reportData.contributions.length > 0) {
        // Add contribution table headers
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        const headers = ['Date', 'Type', 'Amount (GH₵)', 'Payment Method', 'Status'];
        const colWidths = [30, 35, 30, 40, 35];
        let xPosition = margin;
        
        headers.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 8;

        // Add contribution data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        reportData.contributions.forEach(contribution => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = margin;
          const values = [
            contribution.date,
            contribution.type,
            `GH₵${contribution.amount.toFixed(2)}`,
            contribution.paymentMethod,
            contribution.status
          ];

          values.forEach((value, index) => {
            const cellText = String(value || '');
            const lines = doc.splitTextToSize(cellText, colWidths[index] - 2);
            doc.text(lines, xPosition + 1, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 8;
        });
      } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.text('No contributions found for this member.', margin, yPosition);
        yPosition += 10;
      }

      // Add benefits section
      yPosition += 5;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Benefits', margin, yPosition);
      yPosition += 10;

      if (reportData.benefits.length > 0) {
        // Add benefit table headers
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        const headers = ['Request Date', 'Type', 'Amount (GH₵)', 'Status'];
        const colWidths = [40, 45, 40, 45];
        let xPosition = margin;
        
        headers.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 8;

        // Add benefit data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        reportData.benefits.forEach(benefit => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = margin;
          const values = [
            benefit.requestDate,
            benefit.type,
            `GH₵${benefit.amount.toFixed(2)}`,
            benefit.status
          ];

          values.forEach((value, index) => {
            const cellText = String(value || '');
            const lines = doc.splitTextToSize(cellText, colWidths[index] - 2);
            doc.text(lines, xPosition + 1, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 8;
        });
      } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.text('No benefits found for this member.', margin, yPosition);
        yPosition += 10;
      }

      // Add footer
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175); // Gray 500
      doc.text(`Generated by Ghana Town Membership System • ${new Date().toLocaleDateString()}`, margin, pageHeight - 10);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, pageHeight - 10);

      // Return as Blob
      const pdfBlob = new Blob([doc.output('blob')], { type: 'application/pdf' });
      return pdfBlob;
    } catch (error) {
      console.error('Single member financial PDF generation error:', error);
      throw new Error('Failed to generate single member financial PDF report');
    }
  }

  // Generate Financial Report PDF (NEW METHOD)
  async generateFinancialReport(
    contributions: Contribution[],
    expenses: Expense[],
    towns: Town[],
    members: Member[],
    selectedTownId?: string,
    template?: PDFReportTemplate
  ): Promise<Blob> {
    try {
      const reportData = this.generateFinancialReportData(contributions, expenses, towns, members, selectedTownId, template);
      
      // Create PDF using jsPDF
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({
        orientation: reportData.template.layout.pageOrientation,
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = 40;

      // Add header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(37, 99, 235); // Primary blue
      doc.text(reportData.template.header.title, margin, yPosition);
      
      yPosition += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55); // Gray 800
      doc.text(`Town: ${reportData.template.header.townName}`, margin, yPosition);
      doc.text(`Generated: ${reportData.template.header.generatedDate}`, margin + 100, yPosition);
      yPosition += 15;

      // Add financial summary
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Financial Summary', margin, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Total Revenue: GH₵${reportData.data.summary.totalRevenue.toLocaleString()}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Total Expenses: GH₵${reportData.data.summary.totalExpenses.toLocaleString()}`, margin, yPosition);
      yPosition += 8;
      doc.text(`Net Balance: GH₵${reportData.data.summary.balance.toLocaleString()}`, margin, yPosition);
      yPosition += 15;

      // Add contributions section
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Contributions', margin, yPosition);
      yPosition += 10;

      if (reportData.data.contributions.length > 0) {
        // Add contribution table headers
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        const headers = ['Date', 'Member', 'Type', 'Amount', 'Status'];
        const colWidths = [30, 40, 30, 30, 30];
        let xPosition = margin;

        headers.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 8;

        // Add contribution data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        reportData.data.contributions.forEach(contribution => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = margin;
          const values = [
            contribution.date,
            contribution.memberName,
            contribution.type,
            `GH₵${contribution.amount.toLocaleString()}`,
            contribution.status
          ];

          values.forEach((value, index) => {
            const cellText = String(value || '');
            const lines = doc.splitTextToSize(cellText, colWidths[index] - 2);
            doc.text(lines, xPosition + 1, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 8;
        });
      } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.text('No contributions found for this period.', margin, yPosition);
        yPosition += 10;
      }

      // Add expenses section
      yPosition += 5;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Expenses', margin, yPosition);
      yPosition += 10;

      if (reportData.data.expenses.length > 0) {
        // Add expense table headers
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        const headers = ['Date', 'Category', 'Description', 'Amount', 'Beneficiary'];
        const colWidths = [30, 30, 40, 30, 30];
        let xPosition = margin;

        headers.forEach((header, index) => {
          doc.text(header, xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += 8;

        // Add expense data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        
        reportData.data.expenses.forEach(expense => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          xPosition = margin;
          const values = [
            expense.date,
            expense.category,
            expense.description,
            `GH₵${expense.amount.toLocaleString()}`,
            expense.beneficiary
          ];

          values.forEach((value, index) => {
            const cellText = String(value || '');
            const lines = doc.splitTextToSize(cellText, colWidths[index] - 2);
            doc.text(lines, xPosition + 1, yPosition);
            xPosition += colWidths[index];
          });
          yPosition += 8;
        });
      } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.text('No expenses found for this period.', margin, yPosition);
        yPosition += 10;
      }

      // Add footer
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175); // Gray 500
      doc.text(`Generated by Ghana Town Membership System • ${new Date().toLocaleDateString()}`, margin, pageHeight - 10);

      // Return as Blob
      const pdfBlob = new Blob([doc.output('blob')], { type: 'application/pdf' });
      return pdfBlob;
    } catch (error) {
      console.error('Financial PDF generation error:', error);
      throw new Error('Failed to generate financial PDF report');
    }
  }
}

// Export singleton instance
export const pdfExportService = new PDFExportService();